import React, { useState, useMemo } from 'react';
import { TechlogEntry } from '../types';
import { loadData } from '../utils/storage';

interface TechlogSummaryProps {
  refreshTrigger: number;
  onEditEntry?: (entry: TechlogEntry) => void;
}

const TechlogSummary: React.FC<TechlogSummaryProps> = ({ refreshTrigger, onEditEntry }) => {
  const [techlogEntries, setTechlogEntries] = useState<TechlogEntry[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<keyof TechlogEntry>('date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [expandedEntry, setExpandedEntry] = useState<string | null>(null);

  React.useEffect(() => {
    const fetchData = async () => {
      const data = await loadData();
      setTechlogEntries(data.techlogEntries);
    };
    fetchData();
  }, [refreshTrigger]);

  const filteredAndSortedEntries = useMemo(() => {
    let filtered = techlogEntries.filter(entry => 
      (entry.techlogNumber?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
      (entry.aircraftRegistration?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
      (entry.pilotName?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
      (entry.coPilotName?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
      (entry.flightType?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
      (entry.date || '').includes(searchTerm)
    );

    filtered.sort((a, b) => {
      let aValue: any = a[sortField];
      let bValue: any = b[sortField];
      
      if (sortField === 'date') {
        aValue = new Date(a.date).getTime();
        bValue = new Date(b.date).getTime();
      }
      
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [techlogEntries, searchTerm, sortField, sortDirection]);

  const handleSort = (field: keyof TechlogEntry) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatTime = (hours: number): string => {
    return `${hours.toFixed(1)}h`;
  };

  const calculateTotalFlightTime = (sectors: any[]): number => {
    return sectors.reduce((total, sector) => total + sector.flightTime, 0);
  };

  const getSortIcon = (field: keyof TechlogEntry) => {
    if (sortField !== field) return '↕️';
    return sortDirection === 'asc' ? '↑' : '↓';
  };

  const handleRowClick = (entryId: string) => {
    setExpandedEntry(expandedEntry === entryId ? null : entryId);
  };

  if (techlogEntries.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-6">Techlog Summary</h2>
        <div className="text-center text-gray-500 py-8">
          <p>No techlog entries available. Add some entries to see the summary.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h2 className="text-2xl font-bold text-gray-800 mb-6">Techlog Summary</h2>
      
      {/* Search and Stats */}
      <div className="mb-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div className="flex-1 max-w-md">
            <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1">
              Search Entries
            </label>
                         <input
               type="text"
               id="search"
               value={searchTerm}
               onChange={(e) => setSearchTerm(e.target.value)}
               placeholder="Search by techlog number, aircraft, pilot, flight type, or date..."
               className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
             />
          </div>
          
          <div className="text-sm text-gray-600">
            <span className="font-medium">{filteredAndSortedEntries.length}</span> of {techlogEntries.length} entries
          </div>
        </div>
      </div>

      {/* Summary Statistics */}
      <div className="mb-6 p-4 bg-gray-50 rounded-lg">
        <h3 className="text-lg font-medium text-gray-800 mb-3">Summary Statistics</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <span className="text-gray-600">Total Entries:</span>
            <span className="ml-2 font-medium">{techlogEntries.length}</span>
          </div>
          <div>
            <span className="text-gray-600">Unique Aircraft:</span>
            <span className="ml-2 font-medium">
              {new Set(techlogEntries.map(e => e.aircraftRegistration)).size}
            </span>
          </div>
          <div>
            <span className="text-gray-600">Unique Pilots:</span>
            <span className="ml-2 font-medium">
              {new Set(techlogEntries.flatMap(e => [e.pilotName, e.coPilotName].filter(Boolean))).size}
            </span>
          </div>
          <div>
            <span className="text-gray-600">Total Sectors:</span>
            <span className="ml-2 font-medium">
              {techlogEntries.reduce((sum, entry) => sum + entry.sectors.length, 0)}
            </span>
          </div>
        </div>
      </div>

      {/* Techlog Table */}
      <div>
        <table className="w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
             <tr>
               <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-8">
               </th>
               <th 
                 className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                 onClick={() => handleSort('techlogNumber')}
               >
                 <div className="flex items-center space-x-1">
                   <span>Techlog #</span>
                   <span className="text-xs">{getSortIcon('techlogNumber')}</span>
                 </div>
               </th>
               <th 
                 className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                 onClick={() => handleSort('date')}
               >
                 <div className="flex items-center space-x-1">
                   <span>Date</span>
                   <span className="text-xs">{getSortIcon('date')}</span>
                 </div>
               </th>
              <th 
                className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('aircraftRegistration')}
              >
                <div className="flex items-center space-x-1">
                  <span>Aircraft</span>
                  <span className="text-xs">{getSortIcon('aircraftRegistration')}</span>
                </div>
              </th>
              <th 
                className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('pilotName')}
              >
                <div className="flex items-center space-x-1">
                  <span>Pilot</span>
                  <span className="text-xs">{getSortIcon('pilotName')}</span>
                </div>
              </th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Type
              </th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Sectors
              </th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Time
              </th>
              {onEditEntry && (
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              )}
            </tr>
          </thead>
                                           <tbody className="bg-white divide-y divide-gray-200">
             {filteredAndSortedEntries.map((entry) => (
               <React.Fragment key={entry.id}>
                 <tr 
                   className="hover:bg-gray-50 cursor-pointer"
                   onClick={() => handleRowClick(entry.id)}
                 >
                   <td className="px-2 py-3 whitespace-nowrap">
                     <span className="text-gray-400 text-sm">
                       {expandedEntry === entry.id ? '▼' : '▶'}
                     </span>
                   </td>
                   <td className="px-3 py-3 whitespace-nowrap">
                     <div className="text-sm font-medium text-gray-900">
                       {entry.techlogNumber || '-'}
                     </div>
                   </td>
                   <td className="px-3 py-3 whitespace-nowrap">
                     <div className="text-sm text-gray-900">
                       {formatDate(entry.date)}
                     </div>
                   </td>
                   <td className="px-3 py-3 whitespace-nowrap">
                     <div className="text-sm font-medium text-gray-900">
                       {entry.aircraftRegistration}
                     </div>
                   </td>
                   <td className="px-3 py-3 whitespace-nowrap">
                     <div className="text-sm text-gray-900">
                       {entry.pilotName}
                     </div>
                   </td>
                   <td className="px-3 py-3 whitespace-nowrap">
                     <div className="text-sm text-gray-600">
                       {entry.flightType.replace('S-Schedule', 'Schedule').replace('C-Charter', 'Charter').replace('ME-Medevac', 'Medevac').replace('T-Training', 'Training').replace('MT-Maintenance', 'Maint')}
                     </div>
                   </td>
                   <td className="px-3 py-3 whitespace-nowrap text-center">
                     <div className="text-sm text-gray-900">
                       {entry.sectors.length}
                     </div>
                   </td>
                  <td className="px-3 py-3 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {formatTime(calculateTotalFlightTime(entry.sectors))}
                    </div>
                  </td>
                  {onEditEntry && (
                    <td className="px-3 py-3 whitespace-nowrap">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onEditEntry(entry);
                        }}
                        className="px-3 py-1 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        Edit
                      </button>
                    </td>
                  )}
                </tr>
                 
                 {/* Expanded Details Row */}
                 {expandedEntry === entry.id && (
                   <tr className="bg-blue-50">
                     <td colSpan={onEditEntry ? 9 : 8} className="px-6 py-4">
                       <div className="space-y-4">
                         <div className="flex justify-between items-center">
                           <h4 className="text-lg font-semibold text-gray-800">
                             Techlog Details: {entry.techlogNumber || 'N/A'}
                           </h4>
                           <span className="text-sm text-gray-500">
                             Click row to collapse
                           </span>
                         </div>
                         
                         {/* Entry Details */}
                         <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm bg-white p-3 rounded-lg">
                           <div>
                             <span className="font-medium text-gray-700">Co-Pilot:</span>
                             <span className="ml-2 text-gray-900">{entry.coPilotName || 'None'}</span>
                           </div>
                           <div>
                             <span className="font-medium text-gray-700">Total Sectors:</span>
                             <span className="ml-2 text-gray-900">{entry.sectors.length}</span>
                           </div>
                           <div>
                             <span className="font-medium text-gray-700">Created:</span>
                             <span className="ml-2 text-gray-900">{formatDate(entry.createdAt.toISOString())}</span>
                           </div>
                         </div>
                         
                         {/* Sectors Details */}
                         <div>
                           <h5 className="font-medium text-gray-700 mb-3">Sectors ({entry.sectors.length})</h5>
                           <div className="space-y-3">
                             {entry.sectors.map((sector, index) => (
                               <div key={sector.id} className="bg-white rounded-lg p-4 border border-gray-200">
                                 <div className="flex justify-between items-center mb-2">
                                   <h6 className="font-medium text-gray-800">Sector {index + 1}: {sector.departure} → {sector.arrival}</h6>
                                   <span className="text-sm text-gray-600">
                                     Flight Time: {formatTime(sector.flightTime)}
                                   </span>
                                 </div>
                                 <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-3">
                                   <div>
                                     <span className="text-gray-600">Departure:</span>
                                     <span className="ml-2 font-medium">{sector.departure}</span>
                                   </div>
                                   <div>
                                     <span className="text-gray-600">Arrival:</span>
                                     <span className="ml-2 font-medium">{sector.arrival}</span>
                                   </div>
                                   <div>
                                     <span className="text-gray-600">Takeoff:</span>
                                     <span className="ml-2 font-medium">{sector.takeoffTime}</span>
                                   </div>
                                   <div>
                                     <span className="text-gray-600">Landing:</span>
                                     <span className="ml-2 font-medium">{sector.landingTime}</span>
                                   </div>
                                 </div>
                                 <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm pt-3 border-t border-gray-200">
                                   <div>
                                     <span className="text-gray-600">ITT:</span>
                                     <span className="ml-2 font-medium">{sector.itt || '-'}</span>
                                   </div>
                                   <div>
                                     <span className="text-gray-600">Fuel Uplift:</span>
                                     <span className="ml-2 font-medium">{sector.fuelUplift ? `${sector.fuelUplift} Ltrs` : '-'}</span>
                                   </div>
                                   <div>
                                     <span className="text-gray-600">Oil Uplift:</span>
                                     <span className="ml-2 font-medium">{sector.oilUplift ? `${sector.oilUplift} qts` : '-'}</span>
                                   </div>
                                   <div>
                                     <span className="text-gray-600">Flight Time:</span>
                                     <span className="ml-2 font-medium">{formatTime(sector.flightTime)}</span>
                                   </div>
                                 </div>
                               </div>
                             ))}
                           </div>
                         </div>
                         
                         {/* Summary */}
                         <div className="bg-gray-100 rounded-lg p-3">
                           <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                             <div>
                               <span className="text-gray-600">Total Flight Time:</span>
                               <span className="ml-2 font-medium">{formatTime(calculateTotalFlightTime(entry.sectors))}</span>
                             </div>
                             <div>
                               <span className="text-gray-600">Total Fuel Uplift:</span>
                               <span className="ml-2 font-medium">
                                 {entry.sectors.reduce((sum, s) => sum + (s.fuelUplift || 0), 0)} Ltrs
                               </span>
                             </div>
                             <div>
                               <span className="text-gray-600">Total Oil Uplift:</span>
                               <span className="ml-2 font-medium">
                                 {entry.sectors.reduce((sum, s) => sum + (s.oilUplift || 0), 0)} qts
                               </span>
                             </div>
                             <div>
                               <span className="text-gray-600">Avg ITT:</span>
                               <span className="ml-2 font-medium">
                                 {(entry.sectors.reduce((sum, s) => sum + (s.itt || 0), 0) / entry.sectors.length).toFixed(1)}°C
                               </span>
                             </div>
                           </div>
                         </div>
                       </div>
                     </td>
                   </tr>
                 )}
               </React.Fragment>
             ))}
           </tbody>
        </table>
      </div>

      {/* Pagination Info */}
      <div className="mt-4 text-sm text-gray-500 text-center">
        Showing {filteredAndSortedEntries.length} entries
        {searchTerm && ` filtered by "${searchTerm}"`}
      </div>
    </div>
  );
};

export default TechlogSummary;
